import {FC, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {GET, POST} from '../../../services/api'
import PointsTrendChart from './components/PointsTrendChart'
import {
  HOTEL_POINTS_SCOPE_LABELS,
  HOTEL_POINTS_SOURCE_LABELS,
  HOTEL_POINTS_VISIBLE_SCOPES,
  HotelPointsRuleScope,
} from './hotelPointsLabels'

type HotelPointsTransactionType = 'earn' | 'redeem' | 'reversal' | 'expire'

type RuleScope = HotelPointsRuleScope

type TransactionItem = {
  type: HotelPointsTransactionType
  amount: number
  created: string
  sourceType?: string | null
}

type DailyRow = {
  date: string
  earned: number
  redeemed: number
  reversed: number
  expired: number
}

type RuleCount = {
  scope: RuleScope
  label: string
  count: number
}

const RULE_SCOPES: RuleScope[] = [...HOTEL_POINTS_VISIBLE_SCOPES]

const SOURCE_LABELS = HOTEL_POINTS_SOURCE_LABELS

const SCOPE_LABELS = HOTEL_POINTS_SCOPE_LABELS

const extractList = (response: any): any[] => {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.dados)) return response.dados
  if (Array.isArray(response)) return response
  return []
}

const absAmount = (value: number) => Math.abs(Number(value) || 0)

const HotelPointsReportContent: FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dateStart, setDateStart] = useState(moment().startOf('month').format('YYYY-MM-DD'))
  const [dateEnd, setDateEnd] = useState(moment().format('YYYY-MM-DD'))
  const [transactions, setTransactions] = useState<TransactionItem[]>([])
  const [ruleCounts, setRuleCounts] = useState<RuleCount[]>([])
  const [configSummary, setConfigSummary] = useState('Sem configuração')
  const [timeGrouping, setTimeGrouping] = useState<'day' | 'month' | 'year'>('day')

  const loadReport = async (start = dateStart, end = dateEnd) => {
    setLoading(true)
    setError('')
    try {
      const [configRes, transactionsRes, ...managementRes] = await Promise.all([
        GET('/hotel-points-config'),
        POST('/hotel-points/transaction/filter', {
          createdFrom: moment(start).startOf('day').toISOString(),
          createdTo: moment(end).endOf('day').toISOString(),
        }),
        ...RULE_SCOPES.map((scope) => GET(`/hotel-points-management?typePoints=${scope}`)),
      ])

      const currentConfig = Array.isArray(configRes?.data)
        ? configRes.data[0]
        : configRes?.data?.data?.[0] ?? configRes?.data

      if (currentConfig) {
        const parts = [
          currentConfig.basePointsEnabled ? `Base ${currentConfig.basePoints || 0}` : 'Base off',
          currentConfig.onlySpecificRules ? 'Apenas regras' : 'Base + regras',
          `Mín. resgate ${currentConfig.minPointsToRedeem || 0}`,
        ]
        setConfigSummary(parts.join(' · '))
      } else {
        setConfigSummary('Sem configuração')
      }

      setTransactions(
        extractList(transactionsRes).map((item: any) => ({
          type: item?.type,
          amount: absAmount(item?.amount),
          created: item?.created || '',
          sourceType: item?.sourceType || null,
        }))
      )

      setRuleCounts(
        RULE_SCOPES.map((scope, index) => ({
          scope,
          label: SCOPE_LABELS[scope],
          count: extractList(managementRes[index]).filter((item) => item?.status !== false).length,
        }))
      )
    } catch (err) {
      console.error(err)
      setTransactions([])
      setRuleCounts([])
      setError('Não foi possível carregar o relatório de Pontos Hotel.')
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    const start = moment().startOf('month').format('YYYY-MM-DD')
    const end = moment().format('YYYY-MM-DD')
    setDateStart(start)
    setDateEnd(end)
    loadReport(start, end)
  }

  useEffect(() => {
    loadReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stats = useMemo(() => {
    let earned = 0
    let redeemed = 0
    let reversed = 0
    let expired = 0
    let earnCount = 0
    let redeemCount = 0
    let reversalCount = 0
    let expireCount = 0

    transactions.forEach((item) => {
      if (item.type === 'earn') {
        earned += item.amount
        earnCount += 1
      }
      if (item.type === 'redeem') {
        redeemed += item.amount
        redeemCount += 1
      }
      if (item.type === 'reversal') {
        reversed += item.amount
        reversalCount += 1
      }
      if (item.type === 'expire') {
        expired += item.amount
        expireCount += 1
      }
    })

    return {earned, redeemed, reversed, expired, earnCount, redeemCount, reversalCount, expireCount}
  }, [transactions])

  const dailyRows = useMemo<DailyRow[]>(() => {
    const grouped: Record<string, DailyRow> = {}
    transactions.forEach((item) => {
      const key = moment(item.created).isValid()
        ? moment(item.created).format('YYYY-MM-DD')
        : ''
      if (!key) return
      if (!grouped[key]) {
        grouped[key] = {date: key, earned: 0, redeemed: 0, reversed: 0, expired: 0}
      }
      if (item.type === 'earn') grouped[key].earned += item.amount
      if (item.type === 'redeem') grouped[key].redeemed += item.amount
      if (item.type === 'reversal') grouped[key].reversed += item.amount
      if (item.type === 'expire') grouped[key].expired += item.amount
    })

    return Object.values(grouped)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((row) => ({
        ...row,
        date: moment(row.date).format('DD/MM/YYYY'),
      }))
  }, [transactions])

  const chartRows = useMemo(() => {
    const grouped: Record<string, DailyRow> = {}
    transactions.forEach((item) => {
      if (!moment(item.created).isValid()) return
      let key = moment(item.created).format('YYYY-MM-DD')
      if (timeGrouping === 'month') key = moment(item.created).format('YYYY-MM')
      if (timeGrouping === 'year') key = moment(item.created).format('YYYY')

      if (!grouped[key]) {
        grouped[key] = {date: key, earned: 0, redeemed: 0, reversed: 0, expired: 0}
      }
      if (item.type === 'earn') grouped[key].earned += item.amount
      if (item.type === 'redeem') grouped[key].redeemed += item.amount
      if (item.type === 'reversal') grouped[key].reversed += item.amount
      if (item.type === 'expire') grouped[key].expired += item.amount
    })

    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date))
  }, [transactions, timeGrouping])

  const sourceRows = useMemo(() => {
    const grouped: Record<string, number> = {}
    transactions
      .filter((item) => item.type === 'earn')
      .forEach((item) => {
        const key = item.sourceType || 'manual'
        grouped[key] = (grouped[key] || 0) + item.amount
      })

    const total = Object.values(grouped).reduce((sum, value) => sum + value, 0)
    return Object.entries(grouped)
      .map(([source, points]) => ({
        source,
        label: SOURCE_LABELS[source] || source,
        points,
        percentage: total > 0 ? Math.round((points / total) * 100) : 0,
      }))
      .sort((a, b) => b.points - a.points)
  }, [transactions])

  if (loading) return <Loading />

  return (
    <div className='row g-5 gx-xxl-12'>
      <div className='card card-xxl-stretch mb-5 mb-xl-12'>
        <div className='card-header py-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Relatório de Pontos Hotel</span>
            <BreadCrumbs />
            <span className='text-muted mt-1 fw-bold fs-7'>
              Acúmulo, resgate, estorno e expiração no período selecionado.
            </span>
          </h3>
        </div>

        <div className='card-body border-bottom'>
          {error && (
            <div className='alert alert-danger d-flex align-items-center p-5 mb-10'>
              <i className='fas fa-exclamation-triangle fs-2hx text-danger me-4'></i>
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-danger'>Erro ao carregar dados</h4>
                <span>{error}</span>
              </div>
            </div>
          )}

          <div className='row g-3 mt-2'>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data inicial'
                inputAttr={{type: 'date'}}
                value={dateStart}
                change={setDateStart}
                maxLength={dateEnd}
                marginTop='0'
              />
            </div>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data final'
                inputAttr={{type: 'date'}}
                value={dateEnd}
                change={setDateEnd}
                minLength={dateStart}
                marginTop='0'
              />
            </div>
            <div className='col-md-4 d-flex align-items-end justify-content-md-end gap-2 mt-0'>
              <Button
                text='Limpar'
                color='danger'
                iconColor='#FFF'
                size='small'
                horizontalPadding={4}
                btnAttr={{type: 'button'}}
                click={handleClearFilters}
                btnClass='mb-0'
              />
              <Button
                text='Filtrar'
                color='primary'
                icon='fas fa-filter'
                iconColor='#FFF'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => loadReport()}
                btnClass='mb-0 fw-bolder'
              />
            </div>
          </div>
        </div>

        <div className='card-body bg-light'>
          <div className='alert alert-info d-flex align-items-start mb-8'>
            <i className='bi bi-info-circle-fill fs-4 me-3 mt-1'></i>
            <div>Configuração atual: {configSummary}</div>
          </div>

          <div className='row g-5 mb-8'>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <span className='text-gray-600 fw-bold fs-6'>Pontos gerados</span>
                  <div className='text-dark fw-bolder fs-2 mt-3'>{stats.earned}</div>
                  <span className='text-muted fw-semibold fs-7'>{stats.earnCount} acúmulos</span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <span className='text-gray-600 fw-bold fs-6'>Pontos resgatados</span>
                  <div className='text-dark fw-bolder fs-2 mt-3'>{stats.redeemed}</div>
                  <span className='text-muted fw-semibold fs-7'>{stats.redeemCount} resgates</span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <span className='text-gray-600 fw-bold fs-6'>Pontos estornados</span>
                  <div className='text-dark fw-bolder fs-2 mt-3'>{stats.reversed}</div>
                  <span className='text-muted fw-semibold fs-7'>
                    {stats.reversalCount} estornos
                  </span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <span className='text-gray-600 fw-bold fs-6'>Pontos expirados</span>
                  <div className='text-dark fw-bolder fs-2 mt-3'>{stats.expired}</div>
                  <span className='text-muted fw-semibold fs-7'>
                    {stats.expireCount} expirações
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className='row g-5 mb-8'>
            {ruleCounts.map((rule) => (
              <div key={rule.scope} className='col-xl-3 col-md-6'>
                <div className='card bg-body shadow-sm border-0 h-100'>
                  <div className='card-body py-6'>
                    <span className='text-muted fw-semibold fs-7'>Regras de {rule.label}</span>
                    <div className='d-flex align-items-end justify-content-between mt-2'>
                      <span className='text-dark fw-bolder fs-2'>{rule.count}</span>
                      <span className='text-muted fs-8'>ativas</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className='row g-5 mb-8'>
            <div className='col-xl-8'>
              <div className='card border border-gray-300 shadow-none h-100'>
                <div className='card-header pt-5 border-0 align-items-center d-flex justify-content-between'>
                  <h3 className='card-title flex-column m-0'>
                    <span className='card-label fw-bolder text-dark'>Evolução de pontos</span>
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      Volume gerado, resgatado, estornado e expirado
                    </span>
                  </h3>
                  <div className='card-toolbar'>
                    <div className='btn-group' role='group'>
                      <button
                        type='button'
                        className={`btn btn-sm ${
                          timeGrouping === 'day' ? 'btn-primary' : 'btn-light-primary'
                        }`}
                        onClick={() => setTimeGrouping('day')}
                      >
                        Dia
                      </button>
                      <button
                        type='button'
                        className={`btn btn-sm ${
                          timeGrouping === 'month' ? 'btn-primary' : 'btn-light-primary'
                        }`}
                        onClick={() => setTimeGrouping('month')}
                      >
                        Mês
                      </button>
                      <button
                        type='button'
                        className={`btn btn-sm ${
                          timeGrouping === 'year' ? 'btn-primary' : 'btn-light-primary'
                        }`}
                        onClick={() => setTimeGrouping('year')}
                      >
                        Ano
                      </button>
                    </div>
                  </div>
                </div>
                <div className='card-body'>
                  {chartRows.length > 0 ? (
                    <PointsTrendChart data={chartRows} grouping={timeGrouping} />
                  ) : (
                    <div className='text-center text-muted py-10'>
                      Dados insuficientes para gerar o gráfico no período.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='col-xl-4'>
              <div className='card border border-gray-300 shadow-none h-100'>
                <div className='card-header pt-5 border-0'>
                  <h3 className='card-title flex-column m-0'>
                    <span className='card-label fw-bolder text-dark'>Origem dos acúmulos</span>
                    <span className='text-muted mt-1 fw-bold fs-7'>Pontos gerados por origem</span>
                  </h3>
                </div>
                <div className='card-body pt-5'>
                  {sourceRows.length > 0 ? (
                    sourceRows.map((item) => (
                      <div key={item.source} className='d-flex align-items-center mb-4'>
                        <div className='w-140px fw-bolder text-gray-800'>{item.label}</div>
                        <div className='flex-grow-1 mx-3'>
                          <div className='progress h-8px bg-light-primary'>
                            <div
                              className='progress-bar bg-primary rounded'
                              role='progressbar'
                              style={{width: `${item.percentage}%`}}
                            ></div>
                          </div>
                        </div>
                        <div className='w-70px text-end fw-bolder text-muted'>{item.points}</div>
                      </div>
                    ))
                  ) : (
                    <div className='text-center text-muted py-10'>Sem acúmulos no período.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='card border border-gray-300 shadow-none'>
            <div className='card-header pt-5 border-0'>
              <h3 className='card-title flex-column m-0'>
                <span className='card-label fw-bolder text-dark'>Totais diários</span>
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Soma de pontos por dia no período
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <Datatable
                data={dailyRows}
                headers={[
                  {key: 'Data', value: 'date', type: 'default'},
                  {key: 'Gerados', value: 'earned', type: 'default'},
                  {key: 'Resgatados', value: 'redeemed', type: 'default'},
                  {key: 'Estornados', value: 'reversed', type: 'default'},
                  {key: 'Expirados', value: 'expired', type: 'default'},
                ]}
                color='primary'
                search
                showControls
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const HotelPointsReport: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Pontos Hotel', path: 'hotel-points', isActive: false},
          {title: 'Relatório', path: 'hotel-points/report', isActive: true},
        ]}
      >
        Relatório de Pontos Hotel
      </PageTitle>
      <HotelPointsReportContent />
    </>
  )
}

export {HotelPointsReport}
